/**
 * LeadDetailPage — full detail view for a single lead.
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  Badge,
  Modal,
  Input,
} from '@/components/common';
import { PageContainer } from '@/components/layout';
import { useTenantDateFormatter } from '@/utils/datetime';
import { getFullName } from '@/utils';
import {
  useLead,
  useUpdateLead,
  useLeadNotes,
  useAddNote,
  useLeadDuplicates,
  useRecomputeInterest,
  type Lead,
  type LeadUpdate,
} from '../api/leads';
import { ConvertLeadDialog } from '../components/ConvertLeadDialog';
import { OverrideInterestDialog } from '../components/OverrideInterestDialog';
import {
  STAGE_LABEL_MAP,
  STAGE_COLOR_MAP,
  INTEREST_LEVEL_LABEL_MAP,
  INTEREST_LEVEL_COLOR_MAP,
  STAGE_TRANSITIONS,
  CONVERTIBLE_STAGES,
  SOURCE_LABEL_MAP,
  type LeadStage,
  type InterestLevel,
} from '../config/ui';

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const BackBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 14px;
  margin-bottom: 16px;
  &:hover { color: ${({ theme }) => theme.colors.text}; }
`;

const HeaderCard = styled(Card)`
  margin-bottom: 16px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const LeadName = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin: 0;
`;

const BadgeRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const StagePill = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ $color }) => $color};
  background: ${({ $color }) => $color}20;
`;

const InterestPill = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ $color }) => $color};
  background: ${({ $color }) => $color}20;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-top: 16px;
`;

const InfoItem = styled.div``;

const InfoLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 4px;
`;

const InfoValue = styled.div`
  font-size: 14px;
  font-weight: 500;
`;

const SectionCard = styled(Card)`
  margin-bottom: 16px;
`;

const ScoreBar = styled.div<{ $pct: number; $color: string }>`
  height: 8px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  overflow: hidden;
  margin-top: 8px;
  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${({ $pct }) => $pct}%;
    background: ${({ $color }) => $color};
    border-radius: 4px;
    transition: width 0.3s ease;
  }
`;

const ReasonsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 8px 0 0;
`;

const ReasonItem = styled.li`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  padding: 4px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  &:last-child { border-bottom: none; }
`;

const TransitionBtns = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 12px;
`;

const WarningBanner = styled.div`
  padding: 12px 16px;
  background: #fef9c3;
  border: 1px solid #fde047;
  border-radius: 8px;
  color: #78350f;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NotesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
`;

const NoteEntry = styled.div`
  border-left: 3px solid ${({ theme }) => theme.colors.border};
  padding-left: 12px;
`;

const NoteText = styled.p`
  margin: 0;
  font-size: 14px;
`;

const NoteMeta = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 4px;
`;

const AddNoteRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

const ConvertSection = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing.md};
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.lg};
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface StageControlsProps {
  lead: Lead;
  onTransition: (stage: string) => void;
}

const StageTransitionControls: React.FC<StageControlsProps> = ({ lead, onTransition }) => {
  const currentStage = lead.stage as LeadStage;

  if (currentStage === 'converted' || currentStage === 'lost') return null;

  let allowedNext: LeadStage[];
  if (currentStage === 'on_hold') {
    allowedNext = lead.previous_stage ? [lead.previous_stage as LeadStage] : [];
  } else {
    allowedNext = STAGE_TRANSITIONS[currentStage] ?? [];
  }

  if (allowedNext.length === 0) return null;

  return (
    <div data-testid="stage-transition-controls" data-flow="lead-stage-controls">
      <InfoLabel>Move to stage:</InfoLabel>
      <TransitionBtns>
        {allowedNext.map((stage) => {
          const color = STAGE_COLOR_MAP[stage];
          const label = currentStage === 'on_hold' ? `Resume → ${STAGE_LABEL_MAP[stage]}` : STAGE_LABEL_MAP[stage];
          return (
            <Button
              key={stage}
              variant="outline"
              size="sm"
              data-testid={`stage-btn-${stage}`}
              data-flow="lead-stage-btn"
              style={{ borderColor: color, color }}
              onClick={() => onTransition(stage)}
            >
              {label}
            </Button>
          );
        })}
      </TransitionBtns>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export const LeadDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const formatDate = useTenantDateFormatter();

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isConvertOpen, setIsConvertOpen] = useState(false);
  const [isOverrideOpen, setIsOverrideOpen] = useState(false);
  const [noteText, setNoteText] = useState('');

  // Edit form state
  const [editForm, setEditForm] = useState<LeadUpdate>({});

  const { data: lead, isLoading, isError } = useLead(id ?? '');
  const { data: notes } = useLeadNotes(id ?? '');
  const { data: duplicates } = useLeadDuplicates(id ?? '', Boolean(lead?.phone));

  const updateMutation = useUpdateLead();
  const addNoteMutation = useAddNote();
  const recomputeMutation = useRecomputeInterest();

  if (isLoading) {
    return (
      <PageContainer title="Loading…">
        <Card padding="lg" data-testid="lead-detail-loading">
          <p style={{ color: '#64748b' }}>Loading lead…</p>
        </Card>
      </PageContainer>
    );
  }

  if (isError || !lead) {
    return (
      <PageContainer title="Lead Not Found">
        <Card padding="lg" data-testid="lead-detail-error">
          <p>The lead you are looking for does not exist or was deleted.</p>
          <Button onClick={() => navigate('/leads')}>Back to Leads</Button>
        </Card>
      </PageContainer>
    );
  }

  const stageColor = STAGE_COLOR_MAP[lead.stage as LeadStage] ?? '#94a3b8';
  const stageLabel = STAGE_LABEL_MAP[lead.stage as LeadStage] ?? lead.stage;
  const interestColor = INTEREST_LEVEL_COLOR_MAP[lead.interest_level as InterestLevel] ?? '#94a3b8';
  const interestLabel = INTEREST_LEVEL_LABEL_MAP[lead.interest_level as InterestLevel] ?? lead.interest_level;
  const scorePct = Math.min(100, Math.max(0, (lead.interest_level_computed_score / 10) * 100));

  const handleTransition = (stage: string) => {
    updateMutation.mutate({ id: lead.id, body: { stage } });
  };

  const handleEditSubmit = () => {
    updateMutation.mutate({ id: lead.id, body: editForm });
    setIsEditOpen(false);
  };

  const handleAddNote = () => {
    if (!noteText.trim()) return;
    addNoteMutation.mutate({ leadId: lead.id, note: noteText });
    setNoteText('');
  };

  const openEdit = () => {
    setEditForm({
      first_name: lead.first_name,
      last_name: lead.last_name,
      email: lead.email,
      phone: lead.phone,
      source: lead.source,
      notes: lead.notes,
      follow_up_date: lead.follow_up_date,
      assigned_to: lead.assigned_to,
      lost_reason: lead.lost_reason,
    });
    setIsEditOpen(true);
  };

  const showConvert = CONVERTIBLE_STAGES.includes(lead.stage as LeadStage);

  return (
    <div data-testid="lead-detail-page" data-flow="lead-detail-page" style={{ width: '100%' }}>
      <BackBtn onClick={() => navigate('/leads')}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Back to Leads
      </BackBtn>

      {/* Header */}
      <HeaderCard padding="lg">
        <HeaderRow>
          <HeaderLeft>
            <LeadName>{getFullName(lead.first_name, lead.last_name)}</LeadName>
            <BadgeRow>
              <StagePill $color={stageColor}>{stageLabel}</StagePill>
              <InterestPill $color={interestColor}>{interestLabel}</InterestPill>
            </BadgeRow>
          </HeaderLeft>
          <Button variant="outline" size="sm" onClick={openEdit}>Edit</Button>
        </HeaderRow>
        <InfoGrid>
          <InfoItem>
            <InfoLabel>Phone</InfoLabel>
            <InfoValue>{lead.phone || '—'}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Email</InfoLabel>
            <InfoValue>{lead.email || '—'}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Source</InfoLabel>
            <InfoValue>{SOURCE_LABEL_MAP[lead.source as keyof typeof SOURCE_LABEL_MAP] ?? lead.source}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Created</InfoLabel>
            <InfoValue>{formatDate(lead.created_at)}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Last Contacted</InfoLabel>
            <InfoValue>{lead.last_contacted ? formatDate(lead.last_contacted) : '—'}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Follow-up Date</InfoLabel>
            <InfoValue>{lead.follow_up_date ? formatDate(lead.follow_up_date) : '—'}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Assigned To</InfoLabel>
            <InfoValue>{lead.assigned_to || '—'}</InfoValue>
          </InfoItem>
          {lead.preferred_branch_id && (
            <InfoItem>
              <InfoLabel>Branch</InfoLabel>
              <InfoValue>{lead.preferred_branch_id}</InfoValue>
            </InfoItem>
          )}
        </InfoGrid>
        {lead.notes && (
          <div style={{ marginTop: 16 }}>
            <InfoLabel>Notes</InfoLabel>
            <InfoValue style={{ fontWeight: 400 }}>{lead.notes}</InfoValue>
          </div>
        )}
      </HeaderCard>

      {/* Stage transitions */}
      <SectionCard padding="lg">
        <CardHeader>
          <CardTitle>Stage</CardTitle>
        </CardHeader>
        <StageTransitionControls lead={lead} onTransition={handleTransition} />
        {(lead.stage === 'converted' || lead.stage === 'lost') && (
          <p style={{ fontSize: 14, color: '#64748b', marginTop: 8 }}>
            This lead is in a terminal stage ({stageLabel}).
          </p>
        )}
      </SectionCard>

      {/* Duplicates */}
      {duplicates && duplicates.length > 0 && (
        <SectionCard padding="lg" data-testid="duplicates-section">
          <WarningBanner data-testid="duplicate-warning-banner">
            ⚠️ {duplicates.length} potential duplicate lead{duplicates.length > 1 ? 's' : ''} found with the same phone number.
          </WarningBanner>
        </SectionCard>
      )}

      {/* Interest Level Card */}
      <SectionCard padding="lg" data-testid="interest-level-card">
        <CardHeader>
          <CardTitle>Interest Level</CardTitle>
        </CardHeader>
        <div data-testid="interest-level-band" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <InterestPill $color={interestColor} style={{ fontSize: 15, padding: '4px 14px' }}>
            {interestLabel}
          </InterestPill>
          <span style={{ fontSize: 13, color: '#64748b' }}>
            Score: {lead.interest_level_computed_score.toFixed(1)} / 10
          </span>
        </div>
        <ScoreBar $pct={scorePct} $color={interestColor} />
        {lead.interest_level_computed_at && (
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>
            Computed: {formatDate(lead.interest_level_computed_at)}
          </div>
        )}

        {lead.interest_level_computed_reasons.length > 0 && (
          <ReasonsList data-testid="interest-reasons-list">
            {lead.interest_level_computed_reasons.map((r) => (
              <ReasonItem key={r.code}>
                <span>{r.code}{r.detail ? ` — ${r.detail}` : ''}</span>
                <Badge variant="default" size="sm">{r.weight > 0 ? `+${r.weight}` : r.weight}</Badge>
              </ReasonItem>
            ))}
          </ReasonsList>
        )}

        {lead.interest_level_manual_override && (
          <div style={{ marginTop: 12, padding: '8px 12px', background: '#fef3c7', borderRadius: 6, fontSize: 13 }}>
            <strong>Manual override:</strong> {INTEREST_LEVEL_LABEL_MAP[lead.interest_level_manual_override as InterestLevel] ?? lead.interest_level_manual_override}
            {lead.interest_level_manual_override_reason && ` — "${lead.interest_level_manual_override_reason}"`}
            {lead.interest_level_manual_override_expires_at && ` (expires ${formatDate(lead.interest_level_manual_override_expires_at)})`}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <Button
            variant="outline"
            size="sm"
            data-testid="override-interest-btn"
            onClick={() => setIsOverrideOpen(true)}
          >
            Override Interest
          </Button>
          <Button
            variant="ghost"
            size="sm"
            data-testid="recompute-interest-btn"
            disabled={recomputeMutation.isPending}
            onClick={() => recomputeMutation.mutate(lead.id)}
          >
            {recomputeMutation.isPending ? 'Recomputing…' : 'Recompute'}
          </Button>
        </div>
      </SectionCard>

      {/* Notes Timeline */}
      <SectionCard padding="lg">
        <CardHeader>
          <CardTitle>Notes</CardTitle>
        </CardHeader>
        <NotesList data-testid="notes-timeline">
          {(notes ?? []).length === 0 && (
            <p style={{ fontSize: 14, color: '#94a3b8' }}>No notes yet.</p>
          )}
          {(notes ?? []).map((note) => (
            <NoteEntry key={note.id}>
              <NoteText>{note.note}</NoteText>
              <NoteMeta>{formatDate(note.created_at)} · {note.actor_id}</NoteMeta>
            </NoteEntry>
          ))}
        </NotesList>
        <AddNoteRow>
          <div style={{ flex: 1 }}>
            <Input
              placeholder="Add a note…"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              data-testid="add-note-input"
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddNote(); } }}
            />
          </div>
          <Button
            size="sm"
            data-testid="add-note-submit"
            disabled={!noteText.trim() || addNoteMutation.isPending}
            onClick={handleAddNote}
          >
            {addNoteMutation.isPending ? '…' : 'Add'}
          </Button>
        </AddNoteRow>
      </SectionCard>

      {/* Convert to Student */}
      {showConvert && (
        <ConvertSection>
          <Button
            data-testid="convert-to-student-btn"
            data-flow="lead-convert-btn"
            onClick={() => setIsConvertOpen(true)}
          >
            Convert to Student
          </Button>
        </ConvertSection>
      )}

      {/* Edit Lead Modal */}
      <Modal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Edit Lead"
        size="lg"
      >
        <FormGrid>
          <Input
            label="First Name"
            value={editForm.first_name ?? ''}
            onChange={(e) => setEditForm((f) => ({ ...f, first_name: e.target.value }))}
          />
          <Input
            label="Last Name"
            value={editForm.last_name ?? ''}
            onChange={(e) => setEditForm((f) => ({ ...f, last_name: e.target.value }))}
          />
          <Input
            label="Phone"
            value={editForm.phone ?? ''}
            onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
          />
          <Input
            label="Email"
            value={editForm.email ?? ''}
            onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
          />
          <Input
            label="Follow-up Date"
            type="date"
            value={editForm.follow_up_date ?? ''}
            onChange={(e) => setEditForm((f) => ({ ...f, follow_up_date: e.target.value }))}
          />
          <Input
            label="Assigned To"
            value={editForm.assigned_to ?? ''}
            onChange={(e) => setEditForm((f) => ({ ...f, assigned_to: e.target.value }))}
          />
        </FormGrid>
        <div style={{ marginTop: 16 }}>
          <Input
            label="Notes"
            value={editForm.notes ?? ''}
            onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
          />
        </div>
        {lead.stage === 'lost' && (
          <div style={{ marginTop: 16 }}>
            <Input
              label="Lost Reason"
              value={editForm.lost_reason ?? ''}
              onChange={(e) => setEditForm((f) => ({ ...f, lost_reason: e.target.value }))}
            />
          </div>
        )}
        <ModalActions>
          <Button variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSubmit} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </ModalActions>
      </Modal>

      <ConvertLeadDialog
        isOpen={isConvertOpen}
        onClose={() => setIsConvertOpen(false)}
        lead={lead}
      />

      <OverrideInterestDialog
        isOpen={isOverrideOpen}
        onClose={() => setIsOverrideOpen(false)}
        leadId={lead.id}
      />
    </div>
  );
};
